import React, { useEffect, useRef, useState, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import {
  renderRaytracedDieToCanvas,
  getRestingTilt,
} from '../utils/diceRaytracer';
import {
  RaytracedDieMaterial,
  getSavedTablePresetId,
  resolveTablePreset,
  subscribeTablePreset,
} from '../utils/tablePresets';
import { themeManager } from '../utils/themeManager';

const settlePulse = keyframes`
  0% { transform: scale(1); }
  45% { transform: scale(1.08) translateY(-4px); }
  100% { transform: scale(1) translateY(0); }
`;

const DieWrapper = styled.div<{
  $isRolling: boolean;
  $isLocked: boolean;
  $variant: 'mini' | 'compact' | 'normal';
  $sizePx?: number;
}>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${p => {
    if (p.$sizePx) return `${p.$sizePx}px`;
    if (p.$variant === 'mini') return 'clamp(48px, 14vw, 76px)';
    if (p.$variant === 'compact') return 'clamp(52px, 6.5vw, 78px)';
    return 'clamp(64px, 16vw, 102px)';
  }};
  height: ${p => {
    if (p.$sizePx) return `${p.$sizePx}px`;
    if (p.$variant === 'mini') return 'clamp(48px, 14vw, 76px)';
    if (p.$variant === 'compact') return 'clamp(52px, 6.5vw, 78px)';
    return 'clamp(64px, 16vw, 102px)';
  }};
  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  border-radius: 18px;
  transition: transform 0.22s cubic-bezier(0.22, 1, 0.36, 1), filter 0.22s ease;

  ${p =>
    !p.$sizePx &&
    css`
      @media (min-width: 1400px) {
        width: ${p.$variant === 'mini' ? '70px' : p.$variant === 'compact' ? '74px' : '108px'};
        height: ${p.$variant === 'mini' ? '70px' : p.$variant === 'compact' ? '74px' : '108px'};
      }
      @media (min-width: 1920px) {
        width: ${p.$variant === 'mini' ? '84px' : p.$variant === 'compact' ? '82px' : '124px'};
        height: ${p.$variant === 'mini' ? '84px' : p.$variant === 'compact' ? '82px' : '124px'};
      }
      @media (max-width: 420px) {
        width: ${p.$variant === 'mini' ? '50px' : p.$variant === 'compact' ? '54px' : '62px'};
        height: ${p.$variant === 'mini' ? '50px' : p.$variant === 'compact' ? '54px' : '62px'};
      }
    `}

  &:hover {
    transform: translateY(-3px) scale(1.04);
    filter: drop-shadow(0 8px 14px rgba(0, 0, 0, 0.32));
  }

  &:active {
    transform: scale(0.95);
  }

  &:focus-visible {
    outline: 3px solid var(--theme-accent, #ffd54f);
    outline-offset: 3px;
  }

  ${p =>
    p.$isLocked &&
    !p.$isRolling &&
    css`
      animation: ${settlePulse} 0.32s cubic-bezier(0.22, 1, 0.36, 1);
    `}
`;

const DieCanvas = styled.canvas`
  width: 100%;
  height: 100%;
  display: block;
  pointer-events: none;
`;

const ValueBadge = styled.span<{ $isLocked: boolean; $variant: 'mini' | 'compact' | 'normal' }>`
  position: absolute;
  bottom: 2px;
  right: 2px;
  min-width: ${p => (p.$variant === 'mini' ? '18px' : '22px')};
  height: ${p => (p.$variant === 'mini' ? '18px' : '22px')};
  padding: 0 5px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  font-size: ${p => (p.$variant === 'mini' ? '10px' : '12px')};
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.02em;
  color: ${p => (p.$isLocked ? '#072611' : '#ffffff')};
  background: ${p =>
    p.$isLocked
      ? 'linear-gradient(135deg, #ffd54f 0%, #69f0ae 100%)'
      : 'rgba(12, 18, 28, 0.82)'};
  border: 1.5px solid ${p => (p.$isLocked ? '#ffffff' : 'rgba(255, 255, 255, 0.35)')};
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.45);
  pointer-events: none;
  z-index: 2;
`;

const KeyShortcutBadge = styled.span`
  position: absolute;
  top: 2px;
  left: 3px;
  width: 17px;
  height: 17px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.52);
  border: 1px solid rgba(255, 255, 255, 0.24);
  color: rgba(255, 255, 255, 0.88);
  font-size: 10px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 2;

  @media (max-width: 640px) {
    display: none;
  }
`;

const UnrolledMark = styled.span<{ $variant: 'mini' | 'compact' | 'normal' }>`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${p => (p.$variant === 'mini' ? '22px' : p.$variant === 'compact' ? '26px' : '32px')};
  font-weight: 800;
  color: rgba(255, 255, 255, 0.92);
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.65);
  pointer-events: none;
  z-index: 2;
`;

export interface Die2DProps {
  value: number;
  isLocked: boolean;
  onClick: () => void;
  isRolling?: boolean;
  variant?: 'normal' | 'compact' | 'mini';
  index: number;
  /** Optional explicit material from parent 3D board */
  material?: RaytracedDieMaterial;
  /** Optional explicit pixel size */
  sizePx?: number;
  /** Show keyboard shortcut number (1..6) in top-left corner */
  showShortcut?: boolean;
}

const Die2D: React.FC<Die2DProps> = ({
  value,
  isLocked,
  onClick,
  isRolling = false,
  variant = 'normal',
  index,
  material,
  sizePx,
  showShortcut = false,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [hoverOffset, setHoverOffset] = useState<{ x: number; y: number; active: boolean }>({
    x: 0,
    y: 0,
    active: false,
  });
  const [presetMaterial, setPresetMaterial] = useState<RaytracedDieMaterial>(
    () => resolveTablePreset(getSavedTablePresetId()).dieMaterial
  );

  // Subscribe to table preset & theme changes if material is not passed explicitly
  useEffect(() => {
    if (material) return;
    const updateMat = () => {
      setPresetMaterial(resolveTablePreset(getSavedTablePresetId()).dieMaterial);
    };
    const unsubPreset = subscribeTablePreset(updateMat);
    const unsubTheme = themeManager.subscribe(updateMat);
    return () => {
      unsubPreset();
      unsubTheme();
    };
  }, [material]);

  const activeMaterial = material || presetMaterial;
  const isUnrolled = value === 0;
  const baseTilt = getRestingTilt(index, isLocked, isUnrolled);

  // Render static or hovered 3D raytraced frame when not rolling
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (isRolling && !isLocked) return;

    const tiltX = baseTilt.tiltX + (hoverOffset.active ? hoverOffset.y * 0.22 : 0);
    const tiltY = baseTilt.tiltY + (hoverOffset.active ? hoverOffset.x * 0.22 : 0);
    const tiltZ = baseTilt.tiltZ;
    const lift = hoverOffset.active ? 0.35 : isLocked ? 0.12 : 0.0;

    renderRaytracedDieToCanvas(
      canvas,
      {
        value,
        isLocked,
        tiltX,
        tiltY,
        tiltZ,
        lift,
        material: activeMaterial,
      },
      !hoverOffset.active
    );
  }, [value, isLocked, isRolling, baseTilt.tiltX, baseTilt.tiltY, baseTilt.tiltZ, hoverOffset, activeMaterial]);

  // Real-time 60fps 3D raytraced tumbling animation when rolling
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !isRolling || isLocked) return;

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      renderRaytracedDieToCanvas(
        canvas,
        {
          value,
          isLocked,
          tiltX: baseTilt.tiltX,
          tiltY: baseTilt.tiltY,
          tiltZ: baseTilt.tiltZ,
          lift: 0,
          material: activeMaterial,
        },
        true
      );
      return;
    }

    let rafId = 0;
    const startTime = performance.now();
    const duration = 520;
    const spinDirX = index % 2 === 0 ? 1 : -1;
    const spinDirY = index % 3 === 0 ? -1 : 1;
    const startSpinX = spinDirX * Math.PI * 4.0 + index * 0.7;
    const startSpinY = spinDirY * Math.PI * 4.0 + index * 1.1;
    const startSpinZ = (index % 2 === 0 ? 1 : -1) * Math.PI * 2.0;

    const animate = (now: number) => {
      const elapsed = now - startTime;
      const t = Math.min(1, elapsed / duration);
      // Cubic ease-out settle toward the exact resting orientation of target `value`
      const easeOut = 1 - Math.pow(1 - t, 3);
      const remaining = 1 - easeOut;

      // Damped vertical bounce (two hops on the table)
      const bounce = Math.abs(Math.sin(t * Math.PI * 2.2)) * Math.pow(1 - t, 1.35);

      renderRaytracedDieToCanvas(
        canvas,
        {
          value: value || ((Math.floor(elapsed / 90) + index) % 6) + 1,
          isLocked: false,
          tiltX: baseTilt.tiltX + startSpinX * remaining,
          tiltY: baseTilt.tiltY + startSpinY * remaining,
          tiltZ: baseTilt.tiltZ + startSpinZ * remaining,
          lift: bounce,
          material: activeMaterial,
        },
        false
      );

      if (t < 1) {
        rafId = requestAnimationFrame(animate);
      } else {
        renderRaytracedDieToCanvas(
          canvas,
          {
            value,
            isLocked,
            tiltX: baseTilt.tiltX,
            tiltY: baseTilt.tiltY,
            tiltZ: baseTilt.tiltZ,
            lift: 0,
            material: activeMaterial,
          },
          true
        );
      }
    };

    rafId = requestAnimationFrame(animate);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [isRolling, isLocked, value, index, baseTilt.tiltX, baseTilt.tiltY, baseTilt.tiltZ, activeMaterial]);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'touch') return;
    const rect = e.currentTarget.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    const ny = ((e.clientY - rect.top) / rect.height) * 2 - 1;
    setHoverOffset({ x: nx, y: ny, active: true });
  }, []);

  const handlePointerLeave = useCallback(() => {
    setHoverOffset(prev => (prev.active ? { x: 0, y: 0, active: false } : prev));
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick();
      }
    },
    [onClick]
  );

  const pixelRes = variant === 'mini' ? 140 : variant === 'compact' ? 160 : 216;

  return (
    <DieWrapper
      $isRolling={isRolling}
      $isLocked={isLocked}
      $variant={variant}
      $sizePx={sizePx}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      onPointerMove={handlePointerMove}
      onPointerLeave={handlePointerLeave}
      role="button"
      tabIndex={0}
      aria-label={`Tärning ${index + 1}: värde ${value === 0 ? '?' : value}${isLocked ? ', låst' : ''}`}
      aria-pressed={isLocked}
      data-testid="die"
    >
      <DieCanvas ref={canvasRef} width={pixelRes} height={pixelRes} aria-hidden="true" />
      {showShortcut && value > 0 && <KeyShortcutBadge aria-hidden="true">{index + 1}</KeyShortcutBadge>}
      {isUnrolled ? (
        <UnrolledMark $variant={variant} aria-hidden="true">
          ?
        </UnrolledMark>
      ) : (
        !isRolling && (
          <ValueBadge $isLocked={isLocked} $variant={variant} aria-hidden="true">
            {isLocked ? `🔒${value}` : value}
          </ValueBadge>
        )
      )}
    </DieWrapper>
  );
};

export const Die3D = Die2D;
export default Die2D;